import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ArrowPathIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useCurrency } from '../context/CurrencyContext';
import TalkToAdvisorButton from './advice/TalkToAdvisorButton';
import AdviceSaveActions from './advice/AdviceSaveActions';

const REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000;

const pickKeyNumber = (keyFacts, formatAmount) => {
  if (!keyFacts) return null;
  const surplusAv =
    keyFacts.surplus_after_visions != null
      ? Number(keyFacts.surplus_after_visions)
      : Number(keyFacts.surplus);
  const surplus = Number(keyFacts.surplus);
  const income = Number(keyFacts.income);
  const spending = Number(keyFacts.spending);
  const monthlyNet = keyFacts.monthly_net != null ? Number(keyFacts.monthly_net) : income - spending;
  const topVision = keyFacts.top_vision || {};
  const financialHealth = keyFacts.financial_health;

  if (financialHealth === 'critical' || surplusAv < 0) {
    return `${formatAmount(Math.abs(surplusAv))}/month overcommitted`;
  }
  if (monthlyNet < 0) {
    return `${formatAmount(Math.abs(monthlyNet))}/month overspend`;
  }
  if (topVision.gap > 0) {
    return `${formatAmount(topVision.gap)} left for ${topVision.title || 'your top goal'}`;
  }
  if (surplus > 0) {
    return `${formatAmount(surplus)}/month surplus`;
  }
  if (topVision.progress_pct != null) {
    return `${Math.round(topVision.progress_pct)}% toward ${topVision.title || 'your goal'}`;
  }
  return null;
};

const isCriticalHealth = (keyFacts) => {
  if (!keyFacts) return false;
  if (keyFacts.financial_health === 'critical') return true;
  const surplusAv =
    keyFacts.surplus_after_visions != null
      ? Number(keyFacts.surplus_after_visions)
      : Number(keyFacts.surplus);
  return Number.isFinite(surplusAv) && surplusAv < 0;
};

const sanitizeSavingsAdvice = (parsed, keyFacts) => {
  if (!isCriticalHealth(keyFacts)) return parsed;
  const transferPattern = /transfer|automatic|surplus toward|save more|put surplus/i;
  const verdict = transferPattern.test(parsed.verdict || '')
    ? 'Your commitments exceed your income — reduce Vision targets before anything else.'
    : parsed.verdict;
  return {
    ...parsed,
    verdict,
    ifDo: transferPattern.test(parsed.ifDo || '')
      ? 'Lower Vision monthly commitments until they fit inside your income.'
      : parsed.ifDo,
    ifDont: transferPattern.test(parsed.ifDont || '')
      ? 'Do not add savings transfers while you are overcommitted.'
      : parsed.ifDont,
    nextStep: transferPattern.test(parsed.nextStep || '')
      ? 'Open Visions and adjust unrealistic target amounts today.'
      : parsed.nextStep,
  };
};

const payloadToAdvice = (payload, keyFacts, priorities, formatAmount) => {
  if (!payload || !payload.verdict) return null;
  return sanitizeSavingsAdvice(
    {
      verdict: payload.verdict,
      number: payload.number,
      ifDo: payload.act_consequence,
      ifDont: payload.inaction_consequence,
      nextStep: payload.next_step_24h,
      dataConfidence: payload.data_confidence,
      escalationTrigger: payload.escalation_trigger,
      depthTier: payload.depth_tier,
      taxFlag: payload.tax_flag,
      productReco: payload.product_reco,
      rawPayload: payload,
      completionState: payload.completion_state,
    },
    keyFacts,
  );
};

const parseAdvice = (raw, keyFacts, priorities, formatAmount, advicePayload) => {
  const fromPayload = payloadToAdvice(advicePayload, keyFacts, priorities, formatAmount);
  if (fromPayload) return fromPayload;

  const lines = (raw || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  let parsed;
  if (lines.length >= 5) {
    parsed = {
      verdict: lines[0],
      number: lines[1],
      ifDo: lines[2],
      ifDont: lines[3],
      nextStep: lines[4],
    };
  } else {
    const verdict =
      lines[0] ||
      (priorities?.[0]
        ? `${priorities[0].charAt(0).toUpperCase()}${priorities[0].slice(1)} — make that your focus this week.`
        : null);

    const number = lines[1] || pickKeyNumber(keyFacts, formatAmount);

    parsed = {
      verdict,
      number,
      ifDo: lines[2] || 'Acting now compounds progress on your goals within 30 days.',
      ifDont: lines[3] || 'Waiting another month resets momentum and pushes targets back.',
      nextStep: lines[4] || 'Take one concrete step in the next 24 hours.',
    };
  }

  return sanitizeSavingsAdvice(parsed, keyFacts);
};

const AuraTakeCard = ({ embedded = false }) => {
  const { formatAmount } = useCurrency();
  const [advice, setAdvice] = useState(null);
  const [canonicalId, setCanonicalId] = useState(null);
  const [completionState, setCompletionState] = useState('pending');
  const [markingDone, setMarkingDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  const loadAdvice = useCallback(async (refresh = false) => {
    setError(null);
    if (refresh) setRefreshing(true);
    try {
      const res = await axios.get('/api/advice/state', {
        timeout: refresh ? 15000 : 10000,
        params: refresh ? { refresh: true } : {},
      });
      const state = res.data?.advice_state;
      if (!state) {
        setAdvice(null);
        return;
      }
      const priorities = state.priorities || [];
      const parsed = parseAdvice(
        state.whats_most_important_now,
        state.key_facts,
        priorities,
        formatAmount,
        state.advice_payload,
      );
      setAdvice(parsed);
      setCanonicalId(state.canonical_recommendation_id || null);
      setCompletionState(
        state.decisions_summary?.pending === 0 && state.decisions_summary?.decisions_made > 0
          ? 'completed'
          : parsed.completionState || 'pending',
      );
    } catch (err) {
      console.error('Failed to load Aura take:', err);
      setError('Aura\'s take is unavailable right now.');
      setAdvice(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [formatAmount]);

  useEffect(() => {
    loadAdvice();
    const intervalId = window.setInterval(loadAdvice, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [loadAdvice]);

  const handleMarkDone = async () => {
    if (!canonicalId || markingDone) return;
    setMarkingDone(true);
    try {
      await axios.post('/api/advice/decisions/complete', {
        recommendation_id: canonicalId,
        surface: 'aura_take',
      });
      setCompletionState('completed');
    } catch (err) {
      console.error('Failed to mark decision complete:', err);
    } finally {
      setMarkingDone(false);
    }
  };

  const sections = useMemo(() => {
    if (!advice) return [];
    return [
      { label: 'If you do this', text: advice.ifDo },
      { label: 'If you don\'t', text: advice.ifDont },
      { label: 'Next 24 hours', text: advice.nextStep },
    ];
  }, [advice]);

  return (
    <section
      className={`surface-emphasis surface-dark p-5 md:p-6 border border-hairline ${embedded ? 'h-full mb-0' : 'mb-10'}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="text-label text-secondary">Aura&apos;s take</p>
        <button
          type="button"
          onClick={() => loadAdvice(true)}
          disabled={refreshing || loading}
          className="inline-flex items-center gap-1 text-label text-pine hover:text-primary disabled:opacity-50 transition-colors"
          title="New insight"
        >
          <ArrowPathIcon className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          New insight
        </button>
      </div>

      {loading ? (
        <div className="space-y-3 animate-pulse" aria-busy="true">
          <div className="h-5 bg-pine/30 rounded w-full" />
          <div className="h-6 bg-pine/20 rounded w-1/3" />
        </div>
      ) : error ? (
        <p className="text-body text-secondary">{error}</p>
      ) : advice?.verdict ? (
        <>
          <p className="font-display text-h3 text-primary leading-snug">{advice.verdict}</p>
          {advice.number && (
            <p className="figure text-h2 text-primary mt-3">{advice.number}</p>
          )}

          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            className="mt-4 inline-flex items-center gap-1.5 text-body text-secondary hover:text-primary transition-colors duration-300"
            aria-expanded={expanded}
          >
            Why?
            <ChevronDownIcon
              className={`w-4 h-4 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
            />
          </button>

          <AnimatePresence initial={false}>
            {expanded && (
              <div className="overflow-hidden">
                <div className="mt-4 pt-4 border-t border-brass space-y-4">
                  <div>
                    <p className="text-label text-secondary mb-1">Verdict</p>
                    <p className="text-body text-primary">{advice.verdict}</p>
                  </div>
                  {advice.number && (
                    <div>
                      <p className="text-label text-secondary mb-1">The number</p>
                      <p className="figure text-body text-primary">{advice.number}</p>
                    </div>
                  )}
                  {sections.map(({ label, text }) => (
                    <div key={label}>
                      <p className="text-label text-secondary mb-1">{label}</p>
                      <p className="text-body text-primary">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </AnimatePresence>

          <TalkToAdvisorButton
            userQuestion={advice.verdict}
            surface="aura_take"
            framing={advice.verdict}
            advicePayload={advice.rawPayload}
          />

          {advice.rawPayload && (
            <AdviceSaveActions
              advicePayload={advice.rawPayload}
              sourceSurface="aura_take"
              className="mt-3"
            />
          )}

          {canonicalId && completionState !== 'completed' && advice.nextStep && (
            <button
              type="button"
              onClick={handleMarkDone}
              disabled={markingDone}
              className="mt-3 btn-primary text-label disabled:opacity-50"
            >
              {markingDone ? 'Saving…' : 'I did this — mark step done'}
            </button>
          )}
          {completionState === 'completed' && (
            <p className="mt-3 text-label text-positive">Step completed — counted as a decision made.</p>
          )}
        </>
      ) : (
        <p className="text-body text-secondary">
          Add cashflow or connect accounts and Aura will name your #1 priority.
        </p>
      )}
    </section>
  );
};

export default AuraTakeCard;
